import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TemplateField } from "shared";
import { api, ApiError } from "../api";
import type { CaseProgram } from "../caseApi";
import { ShareControls } from "./ShareControls";

const AUTOSAVE_MS = 800;
type Status = "draft" | "ready" | "sent";
type Answers = Record<string, string>;
export type WorksheetView = {
  id: string; caseId: string; status: Status; templateVersion: number;
  template: { title: string; instructions: string; fields: TemplateField[] };
  answers: Answers; missing: string[]; updatedAt: string;
};
export type WorksheetRow = { id: string; programSlug: string | null; programName: string | null; status: Status; templateVersion: number; updatedAt: string; missing: number };

export const worksheetsKey = (caseId: string) => ["worksheets", caseId] as const;
export const worksheetKey = (id: string) => ["worksheet", id] as const;
export const STATUS_TEXT: Record<Status, string> = { draft: "Draft", ready: "Ready", sent: "Sent" };

class NotSaved extends Error {}

/** The keys whose value differs from the saved answers (an emptied answer counts: it clears the stored one). */
function changed(text: Answers, saved: Answers): Answers {
  const out: Answers = {};
  for (const k of new Set([...Object.keys(text), ...Object.keys(saved)])) if ((text[k] ?? "") !== (saved[k] ?? "")) out[k] = text[k] ?? "";
  return out;
}
const missingKeys = (fields: TemplateField[], a: Answers) => fields.filter((f) => f.required && !(a[f.key] ?? "").trim()).map((f) => f.key);

/** One program with a published template: a button that creates the case's worksheet, or opens the one already there. */
function PrepareButton({ program, state, existing, disabled, onOpen, onCreate, busy }: {
  program: CaseProgram; state: string | null; existing?: WorksheetRow; disabled: boolean; busy: boolean;
  onOpen: (id: string) => void; onCreate: (slug: string) => void;
}) {
  const slug = program.slug!;
  const st = program.stateCode ?? state;
  const t = useQuery({
    queryKey: ["template", slug, st], retry: false,
    queryFn: () => api<{ id: string }>(`/api/templates?programSlug=${encodeURIComponent(slug)}${st ? `&state=${encodeURIComponent(st)}` : ""}`),
  });
  if (!t.data) return null;
  const name = program.name ?? slug;
  return (
    <button type="button" className="btn-ghost border border-slate-300" disabled={disabled || busy} aria-label={`Prepare worksheet for ${name}`}
      onClick={() => (existing ? onOpen(existing.id) : onCreate(slug))}>Prepare worksheet</button>
  );
}

/** Worksheets for a case: prepare one per program that has a published template, list them, and edit one at a time. */
export function WorksheetPanel({ caseId, state, programs, readOnly = false, hasEmail = true }: {
  caseId: string; state: string | null; programs: CaseProgram[]; readOnly?: boolean; hasEmail?: boolean;
}) {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useQuery({ queryKey: worksheetsKey(caseId), queryFn: () => api<{ worksheets: WorksheetRow[] }>(`/api/cases/${caseId}/worksheets`) });
  const rows = list.data?.worksheets ?? [];
  const create = useMutation({
    mutationFn: (programSlug: string) => api<WorksheetView>(`/api/cases/${caseId}/worksheets`, { method: "POST", body: { programSlug } }),
    onSuccess: (w) => { qc.setQueryData(worksheetKey(w.id), w); void qc.invalidateQueries({ queryKey: worksheetsKey(caseId) }); setOpenId(w.id); },
  });
  const withSlug = programs.filter((p) => p.slug);
  if (!withSlug.length && !rows.length) return null;
  return (
    <section className="card" aria-label="Worksheets">
      <h2 className="mb-2 text-sm font-semibold">Worksheets</h2>
      <ul className="mb-2 space-y-2">
        {withSlug.map((p) => (
          <li key={p.programId} className="flex flex-wrap items-center gap-2 text-sm">
            <PrepareButton program={p} state={state} disabled={readOnly} busy={create.isPending}
              existing={rows.find((r) => r.programSlug === p.slug)} onOpen={setOpenId} onCreate={(s) => create.mutate(s)} />
          </li>
        ))}
      </ul>
      {create.isError && <p role="alert" className="mb-2 text-sm text-red-600">Could not prepare the worksheet.</p>}
      {list.isError && <p role="alert" className="mb-2 text-sm text-red-600">Could not load the worksheets.</p>}
      {rows.length === 0 && !list.isLoading && <p className="text-sm text-slate-500">No worksheets yet.</p>}
      <ul className="space-y-1 text-sm">
        {rows.map((r) => {
          const name = r.programName ?? "Worksheet";
          return (
            <li key={r.id} className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{name}</span>
              <span className="text-slate-600 dark:text-slate-400">{STATUS_TEXT[r.status]}</span>
              {r.status !== "sent" && r.missing > 0 && <span className="text-xs text-slate-500">{r.missing} required answer{r.missing === 1 ? "" : "s"} missing</span>}
              <button type="button" className="btn-ghost" aria-label={`Open ${name} worksheet`} aria-expanded={openId === r.id}
                onClick={() => setOpenId(openId === r.id ? null : r.id)}>{openId === r.id ? "Close" : "Open"}</button>
            </li>
          );
        })}
      </ul>
      {openId && <WorksheetEditor key={openId} id={openId} caseId={caseId} readOnly={readOnly} hasEmail={hasEmail} />}
    </section>
  );
}

function WorksheetEditor({ id, caseId, readOnly, hasEmail }: { id: string; caseId: string; readOnly: boolean; hasEmail: boolean }) {
  const q = useQuery({ queryKey: worksheetKey(id), queryFn: () => api<WorksheetView>(`/api/worksheets/${id}`) });
  if (q.isLoading) return <p className="mt-3 text-sm text-slate-500">Loading…</p>;
  if (q.error || !q.data) return <p role="alert" className="mt-3 text-sm text-red-600">Could not open this worksheet.</p>;
  return <Editor view={q.data} caseId={caseId} readOnly={readOnly} hasEmail={hasEmail} />;
}

function Editor({ view, caseId, readOnly, hasEmail }: { view: WorksheetView; caseId: string; readOnly: boolean; hasEmail: boolean }) {
  const qc = useQueryClient();
  const id = view.id;
  const fields = view.template.fields;
  const [text, setText] = useState<Answers>(view.answers);
  const [saved, setSaved] = useState<Answers>(view.answers);
  const [updatedAt, setUpdatedAt] = useState(view.updatedAt);
  const [status, setStatus] = useState<Status>(view.status);
  const [newer, setNewer] = useState<WorksheetView | null>(null);
  const [failed, setFailed] = useState(false);
  const [missingNames, setMissingNames] = useState<string[] | null>(null);
  const [actionFailed, setActionFailed] = useState(false);
  const busy = useRef(false);
  const flight = useRef<Promise<boolean> | null>(null);
  const alive = useRef(true);
  const latest = useRef({ text, saved, updatedAt, newer });
  const ro = useRef(readOnly);
  ro.current = readOnly;
  latest.current = { text, saved, updatedAt, newer };

  /** Keep the cache complete and current, even after this editor is gone. */
  const remember = (w: WorksheetView) => {
    qc.setQueryData<WorksheetView>(worksheetKey(id), w);
    void qc.invalidateQueries({ queryKey: worksheetsKey(caseId) });
  };

  /**
   * Sends what differs from the saved answers, then keeps going while text typed during the flight still differs
   * (each send uses the stamp from the previous response). Runs on after unmount; the cache is written every time.
   */
  const runSave = async (): Promise<boolean> => {
    for (;;) {
      const { text: sentText, saved: base, updatedAt: at, newer: conflict } = latest.current;
      const diff = changed(sentText, base);
      if (conflict || !Object.keys(diff).length) return true;
      busy.current = true;
      try {
        const res = await api<WorksheetView>(`/api/worksheets/${id}/answers`, { method: "PUT", body: { answers: diff, updatedAt: at } });
        remember(res);
        const nextSaved = { ...base, ...diff };
        latest.current = { ...latest.current, saved: nextSaved, updatedAt: res.updatedAt };
        if (alive.current) { setSaved(nextSaved); setUpdatedAt(res.updatedAt); setStatus(res.status); setFailed(false); }
        if (ro.current) return true;
      } catch (e) {
        if (!alive.current) return false;
        const current = e instanceof ApiError && e.status === 409 ? (e.details as { worksheet?: WorksheetView } | undefined)?.worksheet : undefined;
        if (current) { latest.current = { ...latest.current, newer: current }; setNewer(current); }
        else setFailed(true);
        return false;
      } finally {
        busy.current = false;
      }
    }
  };
  /** One save at a time: callers during a flight get the running one (which also covers later typing). */
  const save = (): Promise<boolean> => {
    if (flight.current) return flight.current;
    const p = runSave().finally(() => { flight.current = null; });
    flight.current = p;
    return p;
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  const dirty = !readOnly && Object.keys(changed(text, saved)).length > 0;
  useEffect(() => {
    if (!dirty || newer || failed || busy.current) return;
    const t = setTimeout(() => saveRef.current(), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [text, saved, newer, failed, dirty]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  useEffect(() => {
    alive.current = true; // StrictMode runs the cleanup once before the real mount
    return () => {
      const l = latest.current;
      // A save already in flight carries on by itself and sends the later text when it returns.
      if (!ro.current && !l.newer && !flight.current && Object.keys(changed(l.text, l.saved)).length) void saveRef.current();
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (w: WorksheetView) => { setSaved(w.answers); setUpdatedAt(w.updatedAt); setStatus(w.status); };
  // Keep mine: only the rep's own edits (against the old baseline) go on top of the newer answers.
  const keepMine = () => {
    if (!newer) return;
    const own = changed(latest.current.text, latest.current.saved);
    setText({ ...newer.answers, ...own });
    setSaved(newer.answers); setUpdatedAt(newer.updatedAt); setStatus(newer.status); setNewer(null);
  };
  const takeNewer = () => { if (newer) { setText(newer.answers); apply(newer); setNewer(null); } };

  const act = useMutation({
    mutationFn: async (kind: "ready" | "draft") => {
      // Both status calls: wait for any save in flight, then save what is still unsent, before the status call.
      let ok = await saveRef.current();
      if (ok && Object.keys(changed(latest.current.text, latest.current.saved)).length) ok = await saveRef.current();
      if (!ok) throw new NotSaved();
      return api<WorksheetView>(`/api/worksheets/${id}/${kind}`, { method: "POST" });
    },
    onSuccess: (w) => { remember(w); setMissingNames(null); setActionFailed(false); apply(w); },
    onError: (e) => {
      if (e instanceof NotSaved) return; // the save already showed its own conflict banner or failure message
      const m = e instanceof ApiError && e.status === 400 ? (e.details as { missing?: string[] } | undefined)?.missing : undefined;
      if (m) { setMissingNames(m.map((k) => fields.find((f) => f.key === k)?.label ?? k)); setActionFailed(false); }
      else setActionFailed(true);
    },
  });

  /** After a send the server marks the worksheet sent: refetch it and show that (only called while nothing is unsaved). */
  const refreshAfterSend = async () => {
    const w = await api<WorksheetView>(`/api/worksheets/${id}`);
    remember(w);
    if (alive.current && !Object.keys(changed(latest.current.text, latest.current.saved)).length) apply(w);
  };

  const missing = missingKeys(fields, text);
  const inSync = !dirty;
  const differs = newer ? fields.filter((f) => (newer.answers[f.key] ?? "") !== (text[f.key] ?? "")).map((f) => f.label) : [];
  const set = (k: string, v: string) => { setText((t) => ({ ...t, [k]: v })); setFailed(false); };
  const locked = readOnly || status === "sent";

  return (
    <div className="mt-4 border-t border-slate-200 pt-3 dark:border-slate-800">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{view.template.title}</h3>
        <span className="text-xs text-slate-500" role="status">{inSync && !newer && !failed ? "Saved" : "Not saved yet"}</span>
      </div>
      <p className="mb-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm font-medium dark:border-amber-700 dark:bg-amber-950">
        Do not enter Social Security, bank or card numbers.
      </p>
      {view.template.instructions && <p className="mb-3 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{view.template.instructions}</p>}
      <p className="mb-3 text-sm font-medium">Status: {STATUS_TEXT[status]}</p>
      {newer && (
        <div role="alert" className="mb-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950">
          <p className="font-semibold">Someone else saved a newer version of this worksheet.</p>
          {differs.length > 0 && <p className="mt-1 text-slate-700 dark:text-slate-300">Different answers: {differs.join(", ")}</p>}
          <div className="mt-2 flex gap-2">
            <button type="button" className="btn-primary" onClick={keepMine}>Keep mine</button>
            <button type="button" className="btn-ghost" onClick={takeNewer}>Take newer</button>
          </div>
        </div>
      )}
      {failed && <p role="alert" className="mb-2 text-sm text-red-600">Could not save the worksheet. It will retry when you type.</p>}

      <div className="space-y-4">
        {fields.map((f) => {
          const iid = `ws-${f.key}`;
          const miss = missing.includes(f.key);
          const value = text[f.key] ?? "";
          const describedBy = f.help ? `${iid}-help` : undefined;
          const cls = `input ${miss ? "border-2 border-red-600" : ""}`;
          const common = { id: iid, disabled: locked, "aria-describedby": describedBy, "aria-invalid": miss ? true as const : undefined, "aria-required": f.required || undefined, className: cls };
          return (
            <div key={f.key}>
              {f.type === "yesno" && !f.sensitiveEnterOnOfficialSite ? (
                <fieldset aria-describedby={describedBy} aria-invalid={miss ? true : undefined}>
                  <legend className="label">{f.label}</legend>
                  {f.required && <span className="text-xs font-semibold">Required</span>}
                  <div className={`flex gap-4 rounded-md p-1 ${miss ? "border-2 border-red-600" : ""}`}>
                    {["yes", "no"].map((o) => (
                      <label key={o} className="flex items-center gap-1 text-sm">
                        <input type="radio" name={iid} value={o} checked={value === o} disabled={locked} onChange={() => set(f.key, o)} />
                        {o === "yes" ? "Yes" : "No"}
                      </label>
                    ))}
                  </div>
                </fieldset>
              ) : (
                <>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <label className="label" htmlFor={f.sensitiveEnterOnOfficialSite ? undefined : iid}>{f.label}</label>
                    {f.required && <span className="text-xs font-semibold">Required</span>}
                  </div>
                  {f.sensitiveEnterOnOfficialSite ? (
                    <p className="rounded-md border border-dashed border-slate-400 p-2 text-sm font-medium">Enter this on the official site</p>
                  ) : f.type === "textarea" ? (
                    <textarea {...common} rows={3} maxLength={2000} value={value} onChange={(e) => set(f.key, e.target.value)} />
                  ) : f.type === "select" ? (
                    <select {...common} value={value} onChange={(e) => set(f.key, e.target.value)}>
                      <option value="">Choose…</option>
                      {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input {...common} type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"} maxLength={2000}
                      value={value} onChange={(e) => set(f.key, e.target.value)} />
                  )}
                </>
              )}
              {f.help && <p id={`${iid}-help`} className="mt-1 text-xs text-slate-600 dark:text-slate-400">{f.help}</p>}
              {f.officialFieldRef && <p className="mt-0.5 text-xs text-slate-500">Official form: {f.officialFieldRef}</p>}
            </div>
          );
        })}
      </div>

      {!readOnly && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {status === "sent" ? (
            <>
              <span className="text-sm font-medium">Sent</span>
              <button type="button" className="btn-ghost border border-slate-300" disabled={act.isPending} onClick={() => act.mutate("draft")}>Edit (returns to draft)</button>
            </>
          ) : (
            <>
              <button type="button" className="btn-primary" disabled={act.isPending || missing.length > 0 || !!newer || status === "ready"} onClick={() => act.mutate("ready")}>Mark ready</button>
              {status === "ready" && <button type="button" className="btn-ghost border border-slate-300" disabled={act.isPending} onClick={() => act.mutate("draft")}>Back to draft</button>}
              {missing.length > 0 && <span className="text-sm text-slate-600 dark:text-slate-400">Fill in the required fields to mark this ready.</span>}
            </>
          )}
        </div>
      )}
      {missingNames && <p role="alert" className="mt-2 text-sm text-red-600">Still missing: {missingNames.join(", ")}</p>}
      {actionFailed && <p role="alert" className="mt-2 text-sm text-red-600">Could not update the worksheet status.</p>}
      {!readOnly && (status === "ready" || status === "sent") && (
        <ShareControls id={id} hasEmail={hasEmail} onSent={refreshAfterSend}
          blockedReason={newer ? "Resolve the newer version message above first."
            : dirty || failed ? "Save your changes first. These buttons work once the worksheet shows Saved."
            : act.isPending ? "Updating the worksheet status…" : null} />
      )}
    </div>
  );
}
